import React, { useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { useParams } from 'react-router'
import { createColumnHelper } from '@tanstack/react-table'
import { motion } from 'framer-motion'
import { OutPortal, createHtmlPortalNode } from 'react-reverse-portal'
import FiltersContext from '../../../components/Filters/FiltersContext'
import { Button, TextInput, Table } from '@demandlocal/components'
import { useGetAgencyInvoiceDetails } from '../../../api/agencies/useGetAgencyInvoiceDetails'
import { useDownloadInvoiceDetails } from '../../../api/agencies/useDownloadInvoiceDetails'
import toast from 'react-hot-toast'
import { formatNumber } from '../../../lib/utils/formatNumber'
import { useGetAgencyByAgencyId } from '../../../api/agencies/useGetAgencyByAgencyId'

type InvoiceDetail = {
  invoice_number: string
  month: string
  client: string
  job_number: number
  order_number: number
  store_number: number
  item: string
  item_description: string
  item_amount: number
  net_item_amount: number
}

const columnHelper = createColumnHelper<InvoiceDetail>()

const InvoiceDetails = (): JSX.Element => {
  const { agency_id, invoice_number } = useParams()
  const { data: agency } = useGetAgencyByAgencyId(agency_id)
  const filtersPortalNode = React.useMemo(() => createHtmlPortalNode(), [])
  const [filters, setFilters] = useState<Partial<InvoiceDetail>>({
    invoice_number: invoice_number ?? '',
    month: !invoice_number ? dayjs().subtract(1, 'months').format('YYYY-MM') : '',
  })

  const downloadInvoiceDetailsMutation = useDownloadInvoiceDetails()

  const downloadInvoiceDetailsCSV = (): void => {
    const toastId = toast.loading('Downloading...')
    downloadInvoiceDetailsMutation.mutate(
      { agency_id: Number(agency_id) ?? 0, ...filters },
      {
        onSuccess(data) {
          let csvContent = 'data:text/csv;charset=utf-8,'
          csvContent += `"Invoice Number","Month","Client","Job Number","Order Number","Store Number","Product","Invoice Description","Gross $","Net $"\n`
          csvContent += data?.data
            .map((d: InvoiceDetail) =>
              [
                `"${d.invoice_number}"`,
                `"${d.month}"`,
                `"${d.client}"`,
                `"${d.job_number}"`,
                `"${d.order_number}"`,
                `"${d.store_number}"`,
                `"${d.item}"`,
                `"${d.item_description}"`,
                `"$${formatNumber(d.item_amount)}"`,
                `"$${formatNumber(d.net_item_amount)}"`,
              ].join(','),
            )
            .join('\n')
          const encodedUri = encodeURI(csvContent)
          const link = document.createElement('a')
          link.setAttribute('href', encodedUri)
          link.setAttribute('download', `invoice_details_${agency.name}.csv`)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          toast.success('Done downloading!', {
            id: toastId,
          })
        },
        onError() {
          toast.error('Failed to download', { id: toastId })
        },
      },
    )
  }

  const columns = useMemo(
    () => [
      columnHelper.accessor('invoice_number', {
        header: () => 'Invoice Number',
        cell: ({ row }) => row.original?.invoice_number || '-',
      }),
      columnHelper.accessor('month', {
        header: () => 'Month',
        cell: ({ row }) => row.original?.month || '-',
      }),
      columnHelper.accessor('client', {
        header: () => 'Client',
        cell: ({ row }) => row.original?.client || '-',
      }),
      columnHelper.accessor('job_number', {
        header: () => 'Job Number',
        cell: ({ row }) => row.original?.job_number || '-',
      }),
      columnHelper.accessor('order_number', {
        header: () => 'Order Number',
        cell: ({ row }) => row.original?.order_number || '-',
      }),
      columnHelper.accessor('store_number', {
        header: () => 'Store Number',
        cell: ({ row }) => row.original?.store_number || '-',
      }),
      columnHelper.accessor('item', {
        header: () => 'Product',
        cell: ({ row }) => row.original?.item || '-',
      }),
      columnHelper.accessor('item_description', {
        header: () => 'Invoice Description',
        cell: ({ row }) => row.original?.item_description || '-',
      }),
      columnHelper.accessor('item_amount', {
        header: () => 'Gross $',
        cell: ({ row }) =>
          row.original?.item_amount
            ? Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
              }).format(row.original?.item_amount)
            : '-',
      }),
      columnHelper.accessor('net_item_amount', {
        header: () => 'Net $',
        cell: ({ row }) =>
          row.original?.net_item_amount
            ? Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
              }).format(row.original?.net_item_amount)
            : '-',
      }),
    ],
    [],
  )

  return (
    <motion.div className={'flex flex-1 flex-col pb-8 font-Spartan'}>
      <div className={'flex flex-1 flex-col'}>
        <div className={'pb-4 flex justify-between pb-4'}>
          <div className={'font-bold text-base text-primary-xiketic'}>
            Invoice Details
          </div>
          <div className={'flex gap-4'}>
            <Button onClick={() => downloadInvoiceDetailsCSV()}>Export CSV</Button>
            <OutPortal node={filtersPortalNode} />
          </div>
        </div>
        <FiltersContext
          portalNode={filtersPortalNode}
          setFilters={setFilters}
          defaultValues={filters}>
          <TextInput name={'client'} placeholder={'Client name'} label={'Client name'} />
          <TextInput
            name={'invoice_number'}
            placeholder={'Invoice number'}
            label={'Invoice number'}
            defaultValue={filters.invoice_number}
          />
          <TextInput
            name={'month'}
            placeholder={'Month'}
            label={'Month'}
            type={'month'}
            defaultValue={filters.month}
          />
        </FiltersContext>
        <Table
          defaultSorting={[
            { id: 'month', desc: true },
            { id: 'invoice_number', desc: false },
          ]}
          columns={columns}
          filters={{
            ...filters,
            invoice_number: invoice_number ? invoice_number : filters.invoice_number,
            agency_id: agency_id,
          }}
          fetchFn={useGetAgencyInvoiceDetails}
        />
      </div>
    </motion.div>
  )
}

export default InvoiceDetails
